import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  TIERS,
  COMPARISON_TITLE,
  CONDITION_HEADER,
  COMPARISON_ROWS,
  TIER_SECTIONS,
  FOOTNOTES,
} from "./data";
import { CHECK_ICON, DIAMOND_ICON, CARD_ICONS } from "./icons";
import './style.scss';

// 官方网站链接：https://www.hilton.com.cn/zh-cn/member-benefits
// 用于微信小程序webview展示：展示希尔顿酒店的会员权益
// 文案与图标均取自官网移动端，站内不做任何第三方跳转

// 官网图标为静态 svg，按原样渲染以保持一致
const Icon = ({ html, className }) => (
  <span className={className} dangerouslySetInnerHTML={{ __html: html || "" }} />
);

// 多行文案（官网用 <br> 分行）
const Lines = ({ items }) => items.map((line, i) => (
  <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
));

// 权益详情：点击礼遇卡片后全屏弹出，可横向滑动浏览该会籍的全部礼遇
const BenefitDetail = ({ benefits, index, onClose }) => {
  const wrapRef = useRef(null);

  useEffect(() => {
    // 弹层期间锁定页面滚动
    const targets = [document.body, document.getElementById('container')].filter(Boolean);
    const prev = targets.map(el => el.style.overflow);
    targets.forEach(el => { el.style.overflow = 'hidden'; });
    return () => targets.forEach((el, i) => { el.style.overflow = prev[i]; });
  }, []);

  useEffect(() => {
    // 打开时定位到点击的那一张
    const wrap = wrapRef.current;
    const card = wrap && wrap.children[index];
    if (card) wrap.scrollLeft = card.offsetLeft - (wrap.clientWidth - card.offsetWidth) / 2;
  }, [index]);

  return createPortal(
    <div
      className="hilton-benefit-mask"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="benefit-detail-popup">
        <div className="popup-drag-handle" />
        <div className="benefit-detail-content-wrapper" ref={wrapRef}>
          {benefits.map(item => (
            <div className="benefit-card popup-benefit-card" key={item.title}>
              <Icon className="benefit-card-icon" html={CARD_ICONS[item.title]} />
              <h3 className="benefit-card-title">{item.title}</h3>
              <p className="benefit-card-desc">{item.desc}</p>
            </div>
          ))}
        </div>
        <button type="button" className="benefit-detail-close-btn" aria-label="关闭权益详情" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M5 5l14 14M19 5L5 19" />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
};

// 对比表格中单个会籍列的取值
const ComparisonValue = ({ value }) => {
  if (value.t === 'y') return <Icon className="member-success-icon tier-color" html={CHECK_ICON} />;
  if (value.t === 'd') return <Icon className="diamond-icon" html={DIAMOND_ICON} />;
  if (value.t === 's') return <span className="tier-color-text">{value.v}</span>;
  return '-';
};

export default (props) => {
  const [detailIndex, setDetailIndex] = useState(null);
  const closeDetail = useCallback(() => setDetailIndex(null), []);
  const section = TIER_SECTIONS[0];

  return (
    <div className={`hilton-benefits active-tier-${section.key}`}>
   
    <h1 className="hotel-member-title">
      <img className="hotel-member-logo" src="https://staticfile.badazhou.com/20260921/2a6b7d86474589230bcb11915ddfab84.png" alt="" />
      <span>希尔顿荣誉客会-会员礼遇</span>
    </h1>

      {/* 会员权益对比 */}
      <section className="section-comparison">
        <div className="section-content">
          <h2 className="section-title comparison-title">{COMPARISON_TITLE}</h2>

          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <caption className="sr-only">{COMPARISON_TITLE}</caption>
              <thead>
                <tr>
                  <th className="col-condition"><Lines items={CONDITION_HEADER} /></th>
                  {TIERS.map(tier => (
                    <th className="col-tier" key={tier.key} style={{ '--tier-color': tier.color }}>
                      <div className="tier-label">{tier.name}</div>
                      <div className="tier-desc"><Lines items={tier.requirement} /></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map(row => (
                  <tr key={row.label}>
                    <td className="col-condition">
                      {row.badge && <span className="new-badge">{row.badge}</span>}
                      {row.badge ? ' ' : null}{row.label}
                      {row.desc && <div className="row-desc">{row.desc}</div>}
                    </td>
                    {row.values.map((value, i) => (
                      <td className="col-tier" key={TIERS[i].key} style={{ '--tier-color': TIERS[i].color }}>
                        <ComparisonValue value={value} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="descRemark">
            {FOOTNOTES.map((item, i) => {
              if (item.type === 'ul') {
                return (
                  <ul className="remark-content" key={i}>
                    {item.items.map((li, j) => <li key={j}>{li}</li>)}
                  </ul>
                );
              }
              if (item.type === 'terms') {
                // 官网此处链接至 hilton.com，按要求保留文案、去掉跳转
                return (
                  <div className="remark-item remark-terms" key={i}>
                    {item.prefix}<span className="terms-text">{item.text}</span>
                  </div>
                );
              }
              const cls = ['remark-item', item.noMargin && 'no-margin', item.spacing && 'remark-spacing']
                .filter(Boolean).join(' ');
              return <div className={cls} key={i}>{item.text}</div>;
            })}
          </div>
        </div>
      </section>

      {detailIndex !== null && (
        <BenefitDetail benefits={section.benefits} index={detailIndex} onClose={closeDetail} />
      )}
    </div>
  );
}
